import { useCallback, useEffect, useState } from "react";
import type { Api } from "./api.ts";
import { type EstimateRequest, type EstimateSubject, estimateKey, requestEstimate } from "./layers.ts";
import type { AssetUrl, Estimate, Layer } from "./types.ts";

const URL_TTL_MS = 10 * 60 * 1000;
const BATCH_MAX = 100;
const urlCache = new Map<string, { at: number; p: Promise<AssetUrl> }>();

interface Waiting {
  api: Api;
  ws: string;
  id: string;
  resolve: (u: AssetUrl) => void;
  reject: (e: unknown) => void;
}
let waiting: Waiting[] = [];

/** A screen of pictures asks one by one in the same tick: one call per hundred, not one per picture. */
function flushWaiting(): void {
  const all = waiting;
  waiting = [];
  const groups = new Map<Api, Map<string, Waiting[]>>();
  for (const w of all) {
    const byWs = groups.get(w.api) ?? new Map<string, Waiting[]>();
    groups.set(w.api, byWs);
    byWs.set(w.ws, [...(byWs.get(w.ws) ?? []), w]);
  }
  for (const [api, byWs] of groups) {
    for (const [ws, list] of byWs) {
      for (let i = 0; i < list.length; i += BATCH_MAX) {
        const part = list.slice(i, i + BATCH_MAX);
        api
          .assetUrls(
            ws,
            part.map((w) => w.id),
          )
          .then(
            ({ items }) => {
              const byId = new Map(items.map((u) => [u.id, u]));
              for (const w of part) {
                const u = byId.get(w.id);
                if (u) w.resolve(u);
                else w.reject(Object.assign(new Error("This file is gone."), { status: 404 }));
              }
            },
            (e) => {
              for (const w of part) w.reject(e);
            },
          );
      }
    }
  }
}

/** URLs that came along with another read (a production's picture): the screen takes them without asking. */
export function keepAssetUrls(ws: string, urls: readonly (AssetUrl & { id: string })[]): void {
  const at = Date.now();
  for (const url of urls) {
    const key = `${ws}:${url.id}`;
    const hit = urlCache.get(key);
    if (!hit || at - hit.at >= URL_TTL_MS) urlCache.set(key, { at, p: Promise.resolve(url) });
  }
}

/** Signed URL of an asset version, cached for a while (signed URLs expire). */
export function fetchAssetUrl(api: Api, ws: string, id: string): Promise<AssetUrl> {
  const key = `${ws}:${id}`;
  const hit = urlCache.get(key);
  if (hit && Date.now() - hit.at < URL_TTL_MS) return hit.p;
  const p = new Promise<AssetUrl>((resolve, reject) => {
    if (waiting.length === 0) setTimeout(flushWaiting, 0);
    waiting.push({ api, ws, id, resolve, reject });
  });
  urlCache.set(key, { at: Date.now(), p });
  p.catch(() => urlCache.delete(key));
  return p;
}

export function useAssetUrl(
  api: Api,
  ws: string,
  id: string | null,
): { url: AssetUrl | null; error: boolean; retry: () => void } {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<{ id: string | null; url: AssetUrl | null; error: boolean }>({
    id: null,
    url: null,
    error: false,
  });
  // biome-ignore lint/correctness/useExhaustiveDependencies: attempt explicitly retries a failed URL request
  useEffect(() => {
    if (!id) return;
    let live = true;
    fetchAssetUrl(api, ws, id).then(
      (url) => live && setState({ id, url, error: false }),
      () => live && setState({ id, url: null, error: true }),
    );
    return () => {
      live = false;
    };
  }, [api, ws, id, attempt]);
  const retry = useCallback(() => {
    setState({ id: null, url: null, error: false });
    setAttempt((n) => n + 1);
  }, []);
  return state.id === id ? { url: state.url, error: state.error, retry } : { url: null, error: false, retry };
}

export interface EstimateState {
  estimate: Estimate | null;
  error: string | null;
  /** the API's error code, when it sent one (no_capable_model, no_dialogue, …) */
  code: string | null;
  loading: boolean;
}

const IDLE: EstimateState = { estimate: null, error: null, code: null, loading: false };

/**
 * Cost estimate for one layer of a shot: the shot's own estimate, or the scene's for dialogue. Refetches when the
 * inputs change; a `null` subject (the layer cannot be priced here) or `enabled = false` asks nothing.
 */
export function useEstimate(
  api: Api,
  ws: string,
  subject: EstimateSubject | null,
  layer: Layer,
  req: EstimateRequest,
  enabled: boolean,
): EstimateState {
  const { tier, provider, modelKey } = req;
  const [state, setState] = useState<EstimateState & { key: string }>({ key: "", ...IDLE });
  const key = estimateKey(subject, layer, { tier, provider, modelKey });
  const on = enabled && subject !== null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for subject, layer, tier, provider and model
  useEffect(() => {
    if (!on || !subject) return;
    let live = true;
    setState((s) => ({ ...s, key, loading: true }));
    requestEstimate(api, ws, subject, layer, { tier, provider, modelKey }).then(
      (estimate) => live && setState({ key, ...IDLE, estimate }),
      (e) =>
        live &&
        setState({
          key,
          ...IDLE,
          error: e instanceof Error ? e.message : String(e),
          code: (e as { code?: string } | null)?.code ?? null,
        }),
    );
    return () => {
      live = false;
    };
  }, [api, ws, key, on]);
  return on ? (state.key === key ? state : { ...IDLE, loading: true }) : IDLE;
}
